import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';

import { useGetTemplateCatalogQuery, type CatalogTemplate } from 'features/master/api/templates.api';
import {
  useCreateBusinessRuleMutation,
  useGetBusinessRulesQuery,
  useUpdateBusinessRuleMutation,
} from 'features/notification-config/services/businessRules.api';
import { useCollapsibleSection } from 'shared/hooks/collapsibleSections';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import CalendarInstructionsAdvanced from './CalendarInstructionsAdvanced';
import TemplateMessagePreview from './TemplateMessagePreview';

const countBodyParams = (template: CatalogTemplate | undefined): number => {
  const body = template?.provider?.components?.find((c) => c.type === 'BODY');
  return body?.example?.body_text?.[0]?.length ?? 0;
};

// Mirrors back's reminderParams.ts — must stay in sync.
const REMINDER_PARAM_SOURCES = [
  { value: 'client.firstName', label: 'Nombre del cliente' },
  { value: 'client.lastName', label: 'Apellido del cliente' },
  { value: 'client.fullName', label: 'Nombre completo del cliente' },
  { value: 'meeting.startTime', label: 'Fecha y hora de la reunión' },
  { value: 'meeting.meetingLink', label: 'Link de la reunión' },
  { value: 'executive.name', label: 'Nombre del ejecutivo' },
  { value: 'project.name', label: 'Nombre del proyecto' },
  { value: 'subProject.name', label: 'Nombre del subproyecto' },
] as const;

type ReminderParamSource = (typeof REMINDER_PARAM_SOURCES)[number]['value'];

const RULE_NAME = 'CALENDAR_SCHEDULING';
const ENABLED_RULE_NAME = 'CALENDAR_SCHEDULING_ENABLED';
const DEFAULT_MIN_LEAD_TIME_HOURS = 3;
const DEFAULT_MAX_LEAD_TIME_DAYS = 30;
const DEFAULT_REMINDER_OFFSETS = [60];

interface CalendarSchedulingValue {
  minLeadTimeHours?: number;
  maxLeadTimeDays?: number;
  clientReminderEnable?: boolean;
  clientReminderOffsets?: number[];
  vendorReminderEnable?: boolean;
  vendorReminderOffsets?: number[];
  reminderTemplateName?: string;
  reminderTemplateParams?: ReminderParamSource[];
}

interface Props {
  projectId: number;
  projectName?: string;
}

const readValue = (raw: unknown): CalendarSchedulingValue =>
  raw && typeof raw === 'object' ? (raw as CalendarSchedulingValue) : {};
interface OffsetsEditorProps {
  idPrefix: string;
  offsets: number[];
  disabled: boolean;
  onCommit: (next: number[]) => void;
}

const OffsetsEditor = ({ idPrefix, offsets, disabled, onCommit }: OffsetsEditorProps): ReactElement => {
  const [drafts, setDrafts] = useState<string[]>(offsets.map(String));

  useEffect(() => {
    setDrafts(offsets.map(String));
  }, [offsets]);

  const commit = (next: string[]): void => {
    const parsed = next.map((t) => Number(t.trim())).filter((n) => Number.isFinite(n) && n > 0);
    if (parsed.length === 0) {
      setDrafts(offsets.map(String));
      return;
    }
    if (parsed.length === offsets.length && parsed.every((n, i) => n === offsets[i])) return;
    onCommit(parsed);
  };

  const setAt = (i: number, v: string): void => setDrafts((d) => d.map((x, idx) => (idx === i ? v : x)));

  const removeAt = (i: number): void => {
    const next = drafts.filter((_, idx) => idx !== i);
    setDrafts(next);
    commit(next);
  };

  const add = (): void => {
    const next = [...drafts, '60'];
    setDrafts(next);
    commit(next);
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Minutos antes de la reserva (Min)</Label>
      <div className="space-y-2">
        {drafts.map((d, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="relative w-[90px]">
              <Input
                id={`${idPrefix}-offset-${i}`}
                type="number"
                min={1}
                value={d}
                disabled={disabled}
                onChange={(e) => setAt(i, e.target.value)}
                onBlur={() => commit(drafts)}
                className="w-full pr-10 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-text-muted">
                Min
              </span>
            </div>
            {drafts.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => removeAt(i)}
                className="text-destructive hover:text-destructive"
              >
                Quitar
              </Button>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        className="text-sm font-medium text-text-brand disabled:cursor-not-allowed disabled:opacity-50"
        disabled={disabled}
        onClick={add}
      >
        + agregar otro tiempo de notificación
      </button>
    </div>
  );
};

const CalendarSchedulingSection = ({ projectId, projectName }: Props): ReactElement => {
  const sectionProps = useCollapsibleSection('calendar-scheduling');
  const { data, refetch } = useGetBusinessRulesQuery({
    name: RULE_NAME,
    entityType: 'Project',
    entityId: projectId,
    exact: true,
    page: 1,
    limit: 1,
  });
  const { data: enabledData, refetch: refetchEnabled } = useGetBusinessRulesQuery({
    name: ENABLED_RULE_NAME,
    entityType: 'Project',
    entityId: projectId,
    exact: true,
    page: 1,
    limit: 1,
  });
  const [createRule, { isLoading: isCreating }] = useCreateBusinessRuleMutation();
  const [updateRule, { isLoading: isUpdating }] = useUpdateBusinessRuleMutation();
  const { data: templateCatalog } = useGetTemplateCatalogQuery({ projectId });
  const templates = templateCatalog?.data ?? [];

  const rule = data?.data?.[0] ?? null;
  const value = readValue(rule?.value);

  const enabledRule = enabledData?.data?.[0] ?? null;
  const isActive = enabledRule?.value === true;
  const minLeadTimeHours = value.minLeadTimeHours ?? DEFAULT_MIN_LEAD_TIME_HOURS;
  const maxLeadTimeDays = value.maxLeadTimeDays ?? DEFAULT_MAX_LEAD_TIME_DAYS;
  const clientReminderEnable = Boolean(value.clientReminderEnable);
  const clientReminderOffsets = value.clientReminderOffsets ?? DEFAULT_REMINDER_OFFSETS;
  const vendorReminderEnable = Boolean(value.vendorReminderEnable);
  const vendorReminderOffsets = value.vendorReminderOffsets ?? DEFAULT_REMINDER_OFFSETS;
  const reminderTemplateName = value.reminderTemplateName ?? '';
  const reminderTemplateParams = (value.reminderTemplateParams ?? []) as ReminderParamSource[];
  const selectedTemplate = templates.find((t) => t.name === reminderTemplateName);
  const displayLabel = projectName ? `Calendario ${projectName}` : `Calendario ${projectId}`;

  const [minDraft, setMinDraft] = useState(String(minLeadTimeHours));
  const [maxDraft, setMaxDraft] = useState(String(maxLeadTimeDays));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMinDraft(String(minLeadTimeHours));
    setMaxDraft(String(maxLeadTimeDays));
  }, [minLeadTimeHours, maxLeadTimeDays]);

  const isSaving = isCreating || isUpdating;

  const persistValue = async (nextValue: CalendarSchedulingValue): Promise<void> => {
    if (rule) {
      await updateRule({
        id: rule.id,
        businessRule: { value: nextValue, displayName: displayLabel },
      }).unwrap();
    } else {
      await createRule({
        name: RULE_NAME,
        displayName: displayLabel,
        entityType: 'Project',
        entityId: projectId,
        enabled: true,
        value: nextValue,
      }).unwrap();
    }
    await refetch();
  };

  const persistEnabled = async (next: boolean): Promise<void> => {
    if (enabledRule) {
      await updateRule({ id: enabledRule.id, businessRule: { value: next, displayName: displayLabel } }).unwrap();
    } else {
      await createRule({
        name: ENABLED_RULE_NAME,
        displayName: displayLabel,
        entityType: 'Project',
        entityId: projectId,
        enabled: true,
        value: next,
      }).unwrap();
    }
    await refetchEnabled();
  };

  const handleToggle = async (checked: boolean): Promise<void> => {
    setError(null);
    try {
      await persistEnabled(checked);
      if (checked && !rule) {
        await persistValue({
          minLeadTimeHours: value.minLeadTimeHours ?? DEFAULT_MIN_LEAD_TIME_HOURS,
          maxLeadTimeDays: value.maxLeadTimeDays ?? DEFAULT_MAX_LEAD_TIME_DAYS,
        });
      }
    } catch {
      setError('No se pudo actualizar el agendamiento.');
    }
  };

  const commitTime = async (field: 'minLeadTimeHours' | 'maxLeadTimeDays', raw: string): Promise<void> => {
    const parsed = Number(raw);
    const current = field === 'minLeadTimeHours' ? minLeadTimeHours : maxLeadTimeDays;
    const resetDraft = (): void => {
      if (field === 'minLeadTimeHours') setMinDraft(String(current));
      else setMaxDraft(String(current));
    };

    if (!Number.isFinite(parsed) || parsed <= 0) {
      resetDraft();
      return;
    }
    if (parsed === current) return;

    setError(null);
    try {
      await persistValue({ ...value, [field]: parsed });
    } catch {
      setError('No se pudo actualizar el tiempo de anticipación.');
      resetDraft();
    }
  };

  const handleReminderToggle = async (checked: boolean): Promise<void> => {
    setError(null);
    try {
      await persistValue({ ...value, clientReminderEnable: checked });
    } catch {
      setError('No se pudo actualizar el recordatorio al cliente.');
    }
  };

  const handleVendorToggle = async (checked: boolean): Promise<void> => {
    setError(null);
    try {
      await persistValue({ ...value, vendorReminderEnable: checked });
    } catch {
      setError('No se pudo actualizar el recordatorio al ejecutivo.');
    }
  };

  const commitOffsets = async (
    field: 'clientReminderOffsets' | 'vendorReminderOffsets',
    next: number[]
  ): Promise<void> => {
    setError(null);
    try {
      await persistValue({ ...value, [field]: next });
    } catch {
      setError('No se pudo actualizar los tiempos de recordatorio.');
    }
  };

  const commitTemplateName = async (raw: string): Promise<void> => {
    const trimmed = raw.trim();
    if (trimmed === reminderTemplateName) return;

    setError(null);
    try {
      // Reset param mapping when template changes — slot count may differ.
      await persistValue({ ...value, reminderTemplateName: trimmed, reminderTemplateParams: [] });
    } catch {
      setError('No se pudo actualizar el template.');
    }
  };

  const commitTemplateParam = async (index: number, source: ReminderParamSource): Promise<void> => {
    const updated = [...reminderTemplateParams];
    updated[index] = source;
    setError(null);
    try {
      await persistValue({ ...value, reminderTemplateParams: updated });
    } catch {
      setError('No se pudo actualizar el mapping.');
    }
  };

  const paramCount = countBodyParams(selectedTemplate);

  return (
    <Accordion
      type="single"
      collapsible
      {...sectionProps}
      className="bg-bg-surface border border-border-subtle rounded-xl"
    >
      <AccordionItem value="content" className="border-none">
        <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-bg-surface-raised rounded-xl data-[state=open]:rounded-b-none">
          <span className="flex items-center gap-3">
            <span className="font-semibold text-text-default">Calendario</span>
            <Badge className={isActive ? 'bg-success-light text-success' : 'bg-neutral-100 text-text-subtle'}>
              {isActive ? 'Activo' : 'Inactivo'}
            </Badge>
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-5 pb-5 border-t border-border-subtle">
          <div className="flex flex-col gap-4 pt-4">
            {/* Activar agendamiento */}
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1 pr-2">
                <Label htmlFor="calendar-scheduling-toggle" className="text-sm font-medium">
                  Activar agendamiento
                </Label>
                <p className="text-xs text-text-muted">
                  Permite que el agente agende reuniones en el calendario del ejecutivo.
                </p>
              </div>
              <Switch
                id="calendar-scheduling-toggle"
                checked={isActive}
                disabled={isSaving}
                onCheckedChange={(checked) => {
                  void handleToggle(checked);
                }}
              />
            </div>

            {isActive && (
              <>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label htmlFor="calendar-min-lead" className="text-sm font-medium">
                      Mínima anticipación para agendar (Hrs)
                    </Label>
                    <Input
                      id="calendar-min-lead"
                      type="number"
                      min={1}
                      value={minDraft}
                      disabled={isSaving}
                      onChange={(e) => setMinDraft(e.target.value)}
                      onBlur={() => {
                        void commitTime('minLeadTimeHours', minDraft);
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="calendar-max-lead" className="text-sm font-medium">
                      Máxima anticipación para agendar (Días)
                    </Label>
                    <Input
                      id="calendar-max-lead"
                      type="number"
                      min={1}
                      value={maxDraft}
                      disabled={isSaving}
                      onChange={(e) => setMaxDraft(e.target.value)}
                      onBlur={() => {
                        void commitTime('maxLeadTimeDays', maxDraft);
                      }}
                    />
                  </div>
                </div>
                <CalendarInstructionsAdvanced projectId={projectId} />

                {/* Notificaciones card (contiene una card por recordatorio) */}
                <div className="flex flex-col gap-3 rounded-lg border border-border-subtle p-4">
                  <p className="text-base font-semibold text-text-default">Notificaciones</p>

                  {/* Recordatorio Cliente card */}
                  <div className="flex flex-col gap-3 rounded-lg border border-border-subtle p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1 pr-2">
                        <Label htmlFor="calendar-client-reminder-toggle" className="text-sm font-medium">
                          Recordatorio Cliente
                        </Label>
                        <p className="text-xs text-text-muted">
                          Activar template de recordatorio de agendamiento para el cliente.
                        </p>
                      </div>
                      <Switch
                        id="calendar-client-reminder-toggle"
                        checked={clientReminderEnable}
                        disabled={isSaving}
                        onCheckedChange={(checked) => {
                          void handleReminderToggle(checked);
                        }}
                      />
                    </div>

                    {clientReminderEnable && (
                      <>
                        <OffsetsEditor
                          idPrefix="client"
                          offsets={clientReminderOffsets}
                          disabled={isSaving}
                          onCommit={(next) => {
                            void commitOffsets('clientReminderOffsets', next);
                          }}
                        />

                        <div className="space-y-1">
                          <div className="flex items-center gap-3">
                            <Label
                              htmlFor="calendar-reminder-template"
                              className="whitespace-nowrap text-sm font-medium"
                            >
                              Template WhatsApp (cliente):
                            </Label>
                            <Select
                              value={reminderTemplateName || undefined}
                              disabled={isSaving}
                              onValueChange={(v) => {
                                void commitTemplateName(v);
                              }}
                            >
                              <SelectTrigger id="calendar-reminder-template" className="flex-1">
                                <SelectValue placeholder="Selecciona un template..." />
                              </SelectTrigger>
                              <SelectContent>
                                {templates.map((t) => (
                                  <SelectItem key={`${t.name}::${t.language}`} value={t.name}>
                                    {t.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <p className="text-xs text-text-muted">Si está vacío los recordatorios se omiten.</p>
                        </div>

                        <TemplateMessagePreview template={selectedTemplate} />

                        {reminderTemplateName && paramCount > 0 && (
                          <div className="space-y-2 rounded-md border border-border p-3">
                            <p className="text-xs font-medium">Mapeo de Variables</p>
                            {Array.from({ length: paramCount }, (_, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <span className="min-w-[48px] rounded bg-muted px-2 py-1 text-center text-xs font-medium">
                                  {`{{${i + 1}}}`}
                                </span>
                                <Select
                                  value={reminderTemplateParams[i] ?? undefined}
                                  disabled={isSaving}
                                  onValueChange={(v) => {
                                    void commitTemplateParam(i, v as ReminderParamSource);
                                  }}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Selecciona un campo..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {REMINDER_PARAM_SOURCES.map((opt) => (
                                      <SelectItem key={opt.value} value={opt.value} className="text-xs">
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            ))}
                            {reminderTemplateParams.length > 0 && reminderTemplateParams.length !== paramCount && (
                              <Alert variant="destructive" className="mt-1">
                                <AlertDescription>
                                  El mapping tiene {reminderTemplateParams.length} fuente
                                  {reminderTemplateParams.length === 1 ? '' : 's'} pero el template tiene {paramCount}{' '}
                                  placeholder{paramCount === 1 ? '' : 's'}. Revisá el mapping.
                                </AlertDescription>
                              </Alert>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Recordatorio ejecutivo card (independiente del cliente) */}
                  <div className="flex flex-col gap-3 rounded-lg border border-border-subtle p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1 pr-2">
                        <Label htmlFor="calendar-vendor-reminder-toggle" className="text-sm font-medium">
                          Recordatorio ejecutivo
                        </Label>
                        <p className="text-xs text-text-muted">Notifica al ejecutivo asignado antes de la reunión.</p>
                      </div>
                      <Switch
                        id="calendar-vendor-reminder-toggle"
                        checked={vendorReminderEnable}
                        disabled={isSaving}
                        onCheckedChange={(checked) => {
                          void handleVendorToggle(checked);
                        }}
                      />
                    </div>

                    {vendorReminderEnable && (
                      <OffsetsEditor
                        idPrefix="vendor"
                        offsets={vendorReminderOffsets}
                        disabled={isSaving}
                        onCommit={(next) => {
                          void commitOffsets('vendorReminderOffsets', next);
                        }}
                      />
                    )}
                  </div>
                </div>
              </>
            )}

            {error && (
              <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </p>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default CalendarSchedulingSection;
